import { GetHouseholds } from "../types/api";

// Checks the literal below against `GetHouseholds` without widening its inferred
// type to that (optional-everything) interface — this project is on TypeScript
// 4.8, which predates the `satisfies` operator that would otherwise do this
// directly. Widening would turn e.g. `page: 1` into `page: number | undefined`,
// which breaks every call site below that relies on a concrete default (the
// `useState<number>(... || CONTACTS_LIST_DEFAULT_PARAMS.page)` initializers in
// ContactsPage).
function asGetHouseholds<T extends GetHouseholds>(value: T): T {
  return value;
}

/**
 * The households-list request ContactsPage makes on a first visit, with nothing
 * in the URL query string to override it.
 *
 * Same cache-key reasoning as ROUTE_DETAIL_PARAMS / SAVED_ROUTES_LIST_PARAMS in
 * routeParams.ts: the Workbox NetworkFirst cache is keyed on the full request
 * URL, so the offline prefetch must reproduce this exact query string or the
 * cached entry it creates is never looked up — the contacts list would show
 * empty offline with no error anywhere to explain it.
 *
 * Verified against src/pages/ContactsPage/index.tsx:
 * - page: the `(pageFromUrl && +pageFromUrl) || 1` state default.
 * - perPage: the `perPageFromUrl || "25"` state default (a STRING, per the
 *   `perPageValue` state and the `GetHouseholds.perPage: string` type).
 * - mainPersonFullName: the `searchValueFromUrl || null` state default
 *   (`searchValue`), forwarded as `mainPersonFullName` by getDataHouseHolds.
 * - sortOrder / sortBy: `useSortHouseholds()` defaults `sortByValue` to `null`
 *   when the URL carries no `sortOrder` param; getDataHouseHolds forwards that
 *   as `sortOrder` and derives `sortBy: undefined` from `sortByValue === null`.
 * - search: `currentFilters`, built entirely from `getUrlQueryParam()` reads
 *   that all return `null` with an empty query string. Each field below is
 *   that expression evaluated with every `...FromUrl` variable equal to `null`:
 *   `persons_gender`/`deferred_visit`/`not_visit` fall back to `null` (no `||
 *   undefined`), `categories` resolves to `undefined` via
 *   `sanitizeCategoriesPayload(buildCategoryFilterState(...))` (empty/false
 *   inputs on every branch), and everything else falls back to `undefined`.
 */
export const CONTACTS_LIST_DEFAULT_PARAMS = asGetHouseholds({
  page: 1,
  perPage: "25",
  mainPersonFullName: null,
  sortOrder: null,
  sortBy: undefined,
  search: {
    periodicity: undefined,
    persons_gender: null,
    deferred_visit_start_date: undefined,
    due_visit_start_date: undefined,
    categories: undefined,
    projects_include: undefined,
    projects_exclude: undefined,
    data_range_start_date: undefined,
    data_range_end_date: undefined,
    deferred_visit: null,
    not_visit: null,
    household_ids: undefined,
  },
});
